import React, { useEffect, useState } from "react";
import store from "../../redux/store";
import {
  Button,
  Card,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  SimpleGrid,
  Spacer,
  Text,
  IconButton,
  Box,
  Badge,
} from "@chakra-ui/react";
import { 
  FiChevronLeft, 
  FiChevronRight, 
  FiSearch, 
  FiEdit 
} from "react-icons/fi";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { get_defective_returns } from "../../redux/DefectiveSlice";
import CustomButton from "../../components/CustomButton";
import DisplayData from "../../components/DisplayData";
import GasCylinderLoader from "../../components/GasCylinderLoader";

const DefectiveReturnList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState(10);
  const [page, setPage] = useState(1);
  const [initialValue, setInitialValue] = useState(1);
  const [searchQuery, setSearchQuery] = useState(""); // 🔍 For filtering

  const { defective_returns, total_count,loading } = useSelector(
    (state) => state.defectiveReturns
  );

  useEffect(() => {
    if (location.state?.fromDetails) {
      setPage(location.state.page);
      setRows(location.state.rows);
      setInitialValue(location.state.initialValue);
      store.dispatch(get_defective_returns({ page: location.state.page, limit: location.state.rows }));
    } else {
      store.dispatch(get_defective_returns({ page, limit: rows }));
    }
  }, []);

  const onPrevious = () => {
    if (page > 1) {
      setInitialValue(Number(initialValue) - Number(rows));
      setPage(page - 1);
      store.dispatch(get_defective_returns({ page: page - 1, limit: rows }));
    }
  };

  const onNext = () => {
    if (rows * page < total_count) {
      setInitialValue(Number(initialValue) + Number(rows));
      setPage(page + 1);
      store.dispatch(get_defective_returns({ page: page + 1, limit: rows }));
    }
  };

  const handleEdit = (item, e) => {
    e.stopPropagation();
    navigate("edit", {
      state: {
        returnId: item,
        returnData: item,
        isEdit: true,
        page,
        rows,
        initialValue,
      },
    });
  };

  const hasStatusTwo = (details) => {
    return details && details.some((detail) => detail.status === "2");
  };
  
  const hasStatusThree = (details) => {
    return details && details.some((detail) => detail.status === "3");
  };
  

  const isNewReturn = (item) => {
    return item.status === "0" && (!item.sr_no || item.sr_no === "");
  };

  // 🔍 Filter defective_returns by customer_name
  const filteredReturns = defective_returns.filter((item) =>
    item.customer_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <Card
        bg="#fff"
        paddingInline={5}
        paddingBlock={2}
        display="flex"
        flexDirection={["column", "column", "row"]}
        alignItems={["stretch", "stretch", "center"]}
        gap={3}
        flexWrap="wrap"
      >
        <Heading fontSize="xl" flexShrink={0}>
          Defective Returns
        </Heading>

        <Spacer display={["none", "none", "block"]} />

        <Flex
          direction={["column", "row"]}
          gap={2}
          alignItems="center"
          justifyContent={["flex-start", "flex-end"]}
          flex="1"
        >
          <Select
            placeholder="Rows"
            size="md"
            w={["100%", "100px"]}
            value={rows}
            onChange={(e) => {
              setRows(e.target.value);
              setPage(1);
              setInitialValue(1);
              store.dispatch(get_defective_returns({ page: 1, limit: e.target.value }));
            }}
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </Select>

          {/* 🔍 Search input to filter by Vendor Name */}
          <InputGroup bg="#f4f5f7" borderRadius={5} w={["100%", "auto"]} maxW="sm">
            <Input
              placeholder="Search by Vendor Name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <InputRightElement>
              <FiSearch />
            </InputRightElement>
          </InputGroup>

          <CustomButton
            title={"Add Defective"}
            onClick={() => navigate("add")}
            w={["100%", "auto"]}
          />
        </Flex>
      </Card>

{loading ?
(<GasCylinderLoader 
  size="sm" loadingText="Loading Defective returns"/>
):(
  <>
  <SimpleGrid minChildWidth="xs" spacing="10px" mt={4}>
  {filteredReturns.map((item) => (
    <Card
      key={item.id}
      paddingInline={5}
      paddingBlock={2}
      position="relative"
      cursor="pointer"
      _hover={{ boxShadow: "md", transform: "translateY(-3px)" }}
      transition="all 0.2s ease"
      onClick={() =>
        navigate("detail", {
          state: {
            returnData: item,
            page,
            rows,
            initialValue,
          },
        })
      }
    >
      {/* ✨ Edit button (if status = 2) */}
      {hasStatusTwo(item.details) && (
        <IconButton
          icon={<FiEdit />}
          position="absolute"
          top={2}
          right={2}
          size="sm"
          onClick={(e) => handleEdit(item, e)}
          aria-label="Edit defective return"
        />
      )}

{hasStatusThree(item.details) && (
  <Badge
    position="absolute"
    top={hasStatusTwo(item.details) ? "40px" : "8px"}
    right={2}
    colorScheme="blue"
    fontSize="xs"
    px={2}
    py={1}
    borderRadius="md"
  >
    Completed
  </Badge>  
)}

      {/* ✅ Move "New Received" badge below edit icon */}
      {isNewReturn(item) && (
        <Badge
          position="absolute"
          top={hasStatusTwo(item.details) ? "40px" : "8px"} // moves down if edit icon exists
          right={2}
          colorScheme="green"
          fontSize="xs"
          px={2}
          py={1}
          borderRadius="md"
        >
          New Received
        </Badge>
      )}

      {/* Customer Name */}
      <DisplayData
        fs="16px"
        fw="bold"
        v_color="#007bc9"
        value={item.customer_name || "NA"}
      />

      {/* Other details */}
      <Box mt={1}>
        {[
          { title: "Invoice No", value: item.sr_no },
          {
            title: "Defective Return Date",
            value: new Date(item.createdat).toLocaleString(),
          },
          { title: "Ref. Order ID", value: item.orderid },
          { title: "Ref. Invoice Date", value: item.sr_date },
          { title: "Ref. Return Date", value: item.def_return_date },
        ].map((field, idx) => (
          <Flex
            key={idx}
            justify="space-between"
            align="center"
            mb="1"
            wrap="nowrap"
          >
            <Text fontSize="sm" color="gray.600" flex="1" noOfLines={1}>
              {field.title}
            </Text>
            <Text
              fontSize="sm"
              color="gray.800"
              flex="1"
              textAlign="right"
              noOfLines={1}
            >
              {field.value || "NA"}
            </Text>
          </Flex>
        ))}
      </Box>
    </Card>
  ))}
</SimpleGrid>


      <Flex justify="flex-end" mt={4}>
        <Card
          alignItems="center"
          flexDirection="row"
          gap={4}
          py={1}
          px={4}
          border="1px solid #E2E8F0"
          boxShadow="sm"
          borderRadius="md"
          bg="white"
        >
          <FiChevronLeft
            cursor="pointer"
            color={page === 1 ? "gray" : "#007bc9"}
            size={28}
            onClick={onPrevious}
          />

          <Flex align="center" gap={2} fontSize="sm" fontWeight="medium">
            <Text>{initialValue}</Text>
            <Text>-</Text>
            <Text>
              {rows * page >= total_count ? total_count : rows * page}
            </Text>
            <Text>of</Text>
            <Text>{total_count}</Text>
          </Flex>

          <FiChevronRight
            cursor="pointer"
            color={rows * page >= total_count ? "gray" : "#007bc9"}
            size={28}
            onClick={onNext}
          />
        </Card>
      </Flex>
  </>)}

    </>
  );
};

export default DefectiveReturnList;
